import { FactionSelectScene } from '../../scenes';
import {
  FactionSelectOverlay,
  ModeSelectOverlay,
  LoadGameOverlay
} from '../../ui';
import type { RenderPipeline } from '../../rendering';
import { defaultSelection } from '../factions';
import type { FactionId, FactionSelection } from '../factions';
import type { SaveData } from '../save';

export interface FactionHubDependencies {
  readonly container: HTMLElement;
  readonly pipeline: RenderPipeline;
  readonly getAspect: () => number;
  /** Validate + parse a raw save file string. Provided by the caller
   *  (Application) so the FactionHub doesn't pull in SaveGameService
   *  directly — keeps the dependency one-way. Throws on invalid
   *  payloads; the LoadGameOverlay surfaces the error. */
  readonly parseSave: (raw: string) => SaveData;
}

/** Result of running the intro flow. `saveData` is null for a New
 *  Game; populated when the player picked Load Game and supplied a
 *  valid save file. */
export interface FactionHubResult {
  readonly selection: FactionSelection;
  readonly saveData: SaveData | null;
}

/**
 * Intro flow orchestrator. Drives the four-stage entry sequence:
 *
 *   1. Mode select          — Singleplayer (selectable) / Cooperative (locked)
 *   2. Faction select       — only Pirates is unlocked in v0
 *   3. Load or new game     — Load opens a placeholder save list
 *   4. Ship customisation   — colour palette + Enter sector
 *
 * Steps 2 and 4 share a single `FactionSelectOverlay` flipped between
 * `'faction'` and `'customize'` stages, so the 3D ship preview stays
 * smoothly visible across both. Steps 1 and 3 are full-screen overlays
 * that sit on top of the same render loop.
 */
export class FactionHub {
  private readonly scene: FactionSelectScene;
  private readonly pipeline: RenderPipeline;
  private readonly getAspect: () => number;
  private readonly container: HTMLElement;
  private readonly deps: FactionHubDependencies;

  private selection: FactionSelection = defaultSelection();
  private running = false;
  private rafId = 0;
  private lastTime = 0;
  private readonly handleResize: () => void;

  constructor(deps: FactionHubDependencies) {
    this.deps = deps;
    this.container = deps.container;
    this.pipeline = deps.pipeline;
    this.getAspect = deps.getAspect;

    this.scene = new FactionSelectScene(deps.getAspect());
    // The default selection picks Pirates — the only unlocked faction
    // in this build. Swap the default colour to the first pirate option
    // so the ship preview matches the locked-faction guarantee.
    this.scene.setShip(this.selection);

    this.handleResize = (): void => {
      this.scene.setAspect(this.getAspect());
    };
  }

  async run(): Promise<FactionHubResult> {
    window.addEventListener('resize', this.handleResize);
    this.running = true;
    this.lastTime = performance.now();
    this.tick();

    // Stage 1 — mode select.
    await this.runModeSelect();

    // Stage 2 — faction select (Pirates only).
    await this.runFactionSelect();

    // Stage 3 — new game / load game.
    const saveData = await this.runLoadOrNewGame();

    // Stage 4 — ship customisation. Skipped when the player loaded a
    // save: the saved faction + colour come straight off the file.
    if (saveData) {
      this.selection = {
        factionId: saveData.faction.factionId,
        primaryColor: saveData.faction.primaryColor
      };
      this.scene.setShip(this.selection);
      return { selection: this.selection, saveData };
    }

    const finalSelection = await this.runShipCustomize();
    return { selection: finalSelection, saveData: null };
  }

  async dispose(): Promise<void> {
    this.running = false;
    cancelAnimationFrame(this.rafId);
    window.removeEventListener('resize', this.handleResize);
    this.scene.dispose();
  }

  /* ─────────────────────── stages ─────────────────────── */

  private runModeSelect(): Promise<void> {
    return new Promise((resolve) => {
      const overlay = new ModeSelectOverlay({
        onConfirm: async (mode) => {
          if (mode !== 'singleplayer') return;
          await overlay.hideAndDispose();
          resolve();
        }
      });
      this.container.appendChild(overlay.element);
      overlay.show();
    });
  }

  private runFactionSelect(): Promise<void> {
    return new Promise((resolve) => {
      const overlay = new FactionSelectOverlay(
        this.selection,
        {
          onFactionChange: (id) => this.handleFactionChange(id),
          onColorChange: (color) => this.handleColorChange(color),
          onContinue: async () => {
            await overlay.hideAndDispose();
            resolve();
          },
          onConfirm: () => {
            // Not used in this stage; the customise stage uses its own
            // overlay instance (see runShipCustomize).
          }
        },
        {
          // Lock everything except pirates. The overlay renders a
          // "Coming Soon" badge on the locked tabs.
          lockedFactions: ['miners', 'guards']
        }
      );
      overlay.setStage('faction');
      this.container.appendChild(overlay.element);
      overlay.show();
    });
  }

  private runLoadOrNewGame(): Promise<SaveData | null> {
    return new Promise((resolve) => {
      const overlay = new LoadGameOverlay({
        onNewGame: async () => {
          await overlay.hideAndDispose();
          resolve(null);
        },
        onLoadFile: async (raw) => {
          // Parse synchronously here so the overlay can surface a
          // friendly error inline if the file is invalid. On success,
          // close the overlay and pass the parsed data up.
          let parsed: SaveData;
          try {
            parsed = this.parseSaveDeps(raw);
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            overlay.setError(msg);
            return;
          }
          await overlay.hideAndDispose();
          resolve(parsed);
        }
      });
      this.container.appendChild(overlay.element);
      overlay.show();
    });
  }

  private parseSaveDeps(raw: string): SaveData {
    return this.deps.parseSave(raw);
  }

  private runShipCustomize(): Promise<FactionSelection> {
    return new Promise((resolve) => {
      const overlay = new FactionSelectOverlay(
        this.selection,
        {
          onFactionChange: () => {
            // Faction is locked once we reach customisation.
          },
          onColorChange: (color) => this.handleColorChange(color),
          onContinue: () => {
            // Continue button is hidden in customise stage.
          },
          onConfirm: async () => {
            const final = this.selection;
            await overlay.hideAndDispose();
            resolve(final);
          }
        },
        {
          // Only pirates is selectable; the others stay visually locked
          // for consistency with stage 2.
          lockedFactions: ['miners', 'guards']
        }
      );
      overlay.setStage('customize');
      this.container.appendChild(overlay.element);
      overlay.show();
    });
  }

  private handleFactionChange(id: FactionId): void {
    // v0 keeps everyone on Pirates regardless of UI input — but if a
    // future build unlocks the other factions, this will switch the
    // ship preview as expected.
    this.selection = {
      factionId: id,
      primaryColor: this.selection.primaryColor
    };
    this.scene.setShip(this.selection);
  }

  private handleColorChange(color: number): void {
    this.selection = { ...this.selection, primaryColor: color };
    this.scene.setShip(this.selection);
  }

  private tick = (): void => {
    if (!this.running) return;
    const now = performance.now();
    const dt = Math.min((now - this.lastTime) / 1000, 0.1);
    this.lastTime = now;

    this.scene.update(dt);
    this.pipeline.render(this.scene.root, this.scene.camera);

    this.rafId = requestAnimationFrame(this.tick);
  };
}
